import {useEffect, useMemo, useRef, useState} from 'react';
import {Autocomplete, Button, Icon} from '@ezcater/tapas';
import {faLocationArrow, faTriangleExclamation, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {faMapMarkerAlt, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {useField, useFormikContext} from 'formik';
import {debounce} from 'lodash-es';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import poweredByGoogleImage from '@/assets/images/powered_by_google_on_white_hdpi.png';
import {useConsumerCart, useDerivedMenuData} from '@/components/features/menu/hooks';
import {type FulfillmentDetailFormProps} from '@/components/global/layout/AppBar';
import {useFulfillmentDetailState} from '@/components/global/providers/FulfillmentDetailStateProvider';
import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {useGoogleMapsAPI} from '@/components/global/providers/GoogleMapsProvider';
import {useIsDebounceEnabled} from '@/components/global/utilities/DebounceProvider';
import useIdentity from '@/hooks/useIdentity';
import useSavedAddressSearch from '@/hooks/useSavedAddressSearch';
import useTracking from '@/hooks/useTracking';
import {type GeocodedAddressResult, type SavedAddress} from '@/utils/googleAutocomplete';
import AddressSearchAutocompleteExperimentEmptyListbox from './experiment/AddressSearchAutocompleteExperimentEmptyListbox';
import AddressSearchAutocompleteExperimentOption from './experiment/AddressSearchAutocompleteExperimentOption';
import AddressSearchAutocompleteOption from './AddressSearchAutocompleteOption';
import AddressSearchAutocompleteSuffix from './AddressSearchAutocompleteSuffix';
import {
  type AddressSearchAutocompleteProps,
  type AutocompleteOption,
  type AutocompleteOptionGroup,
  type GetPlacePredictionsArgs,
  MANUAL_ENTRY_OPTION_ID,
} from './types';

type ValueChangeDetails = Parameters<
  NonNullable<React.ComponentProps<typeof Autocomplete.Root>['onValueChange']>
>[1];
type InputKeyDownEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Autocomplete.Input>['onKeyDown']>
>[0];
type InputFocusEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Autocomplete.Input>['onFocus']>
>[0];
type InputClickEvent = Parameters<
  NonNullable<React.ComponentProps<typeof Autocomplete.Input>['onClick']>
>[0];

export default function AddressSearchAutocomplete({
  disabled = false,
  corpAccountFilterId,
  error = false,
  geocodedAddressResultName,
  googlePlaceName,
  id,
  inputName,
  onInputChange,
  onManualEntrySelected,
  onOpenChange,
  open,
  prefix,
  savedAddressName,
  shouldIncludeSavedAddresses = false,
  shouldSubmitOnSelect = false,
  showManualEntryOption = false,
  showLocationButton = true,
  appBarStepThrough = false,
  slotProps,
  trackingSubcategory = 'address search',
  validateAddress,
  experimentVariation,
}: AddressSearchAutocompleteProps) {
  const {submitForm, values, setValues} = useFormikContext<FulfillmentDetailFormProps>();
  const [, inputMeta, inputHelpers] = useField<string>(inputName);
  const {value: inputValue} = inputMeta;
  const [googlePlaceOptions, setGooglePlaceOptions] = useState<AutocompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasTypedSinceOpen, setHasTypedSinceOpen] = useState(false);
  const highlightedItemRef = useRef<AutocompleteOption | undefined>(undefined);
  const selectOnNextClickRef = useRef(false);
  const [trackBeganSearchKeyword, setTrackBeganSearchKeyword] = useState(true);
  const [trackShownSavedAddresses, setTrackShownSavedAddresses] = useState(false);
  const {libraries} = useGoogleMapsAPI();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const {t} = useTranslation('common');
  const {track} = useTracking();
  const shouldDebounce = useIsDebounceEnabled();
  const {caterer} = useDerivedMenuData();
  const consumerCart = useConsumerCart();
  const {data: identityData} = useIdentity();
  const corpAccountId = identityData?.me?.consumerAccount?.corporateAccount?.id ?? null;
  const {id: dataCatererId} = caterer ?? {};
  const catererId = dataCatererId ? dataCatererId : null;
  const {fulfillmentDetailClickHandler, FulfillmentDetailStates} = useFulfillmentDetailState();
  const {
    address,
    eventLocalTime,
    eventName,
    eventOn,
    headcount,
    id: fulfillmentDetailId,
    strategy,
  } = fulfillmentDetail ?? {};

  const miscJson = JSON.stringify({
    consumer_cart_id: consumerCart?.id,
    fulfillment_detail_id: fulfillmentDetailId,
    street: address?.street,
    city: address?.city,
    state: address?.state,
    corpAccountId,
    eventName,
    orderType: strategy,
    eventDate: eventOn,
    eventTime: eventLocalTime,
    headcountNumber: headcount,
  });

  // Formik field helpers
  const [, {value: googlePlace}, googlePlaceHelpers] = useField<AutocompleteOption | null>(
    googlePlaceName,
  );
  const [, , geocodedAddressResultHelpers] = useField<GeocodedAddressResult | null>(
    geocodedAddressResultName,
  );
  const [, , savedAddressHelpers] = useField<SavedAddress | null>(savedAddressName as string);

  const {addresses: savedAddresses} = useSavedAddressSearch({
    searchTerm: inputValue || ' ',
    skip: !shouldIncludeSavedAddresses,
    limit: 3,
    corpAccountId: corpAccountFilterId,
  });

  const savedAddressesLabel = t('components.AddressSearchAutocomplete.groups.savedAddresses');
  const searchResultsLabel =
    savedAddresses.length > 0 ? t('components.AddressSearchAutocomplete.groups.searchResults') : '';

  const transformedGooglePlaceOptions = googlePlaceOptions.map(
    option =>
      ({
        description: option.description,
        group: searchResultsLabel,
        place_id: option.place_id,
        structured_formatting: option.structured_formatting,
      }) satisfies AutocompleteOption,
  );

  const transformedSavedAddressOptions = savedAddresses.map(
    option =>
      ({
        corporate: option.corporate,
        description: option.secondaryText,
        group: savedAddressesLabel,
        place_id: option.placeId,
        structured_formatting: {
          main_text: option.mainText,
          secondary_text: option.secondaryText,
        },
      }) satisfies AutocompleteOption,
  );

  const manualEntryOption: AutocompleteOption | null = useMemo(
    () =>
      showManualEntryOption
        ? {
            description: t('components.AddressSearchAutocomplete.enterManually'),
            group: '',
            place_id: MANUAL_ENTRY_OPTION_ID,
            structured_formatting: {
              main_text: t('components.AddressSearchAutocomplete.enterManually'),
              secondary_text: '',
            },
          }
        : null,
    [showManualEntryOption, t],
  );

  const allOptionGroups: AutocompleteOptionGroup[] = [
    {id: 'saved', label: savedAddressesLabel, items: transformedSavedAddressOptions},
    {id: 'search', label: searchResultsLabel, items: transformedGooglePlaceOptions},
    {id: 'manual', label: '', items: manualEntryOption ? [manualEntryOption] : []},
  ];
  const optionGroups = allOptionGroups.filter(group => group.items.length > 0);
  const hasOptions = optionGroups.length > 0;

  // ezx578::ARC::PreciseAddressNudge — until the user types, the popup shows the empty state
  // (use current location + nudge) instead of the results list.
  const showListboxEmpty = !!experimentVariation && (!hasTypedSinceOpen || inputValue === '');
  const hasPopupContent = showListboxEmpty || hasOptions;
  const contentSideOffset =
    typeof slotProps?.content?.sideOffset === 'number' ? slotProps.content.sideOffset : 4;

  const autocompleteService = useMemo(
    () =>
      libraries.places?.AutocompleteService ? new libraries.places.AutocompleteService() : null,
    [libraries.places],
  );

  const fetchGooglePlacePredictions = useMemo(() => {
    const fetcher = (request: GetPlacePredictionsArgs[0], callback: GetPlacePredictionsArgs[1]) => {
      autocompleteService?.getPlacePredictions(request, callback);
    };
    return shouldDebounce ? debounce(fetcher, 400) : fetcher;
  }, [autocompleteService, shouldDebounce]);

  const trackAddressSelected = (option: AutocompleteOption, isSavedAddress: boolean) => {
    track('address selected', {
      caterer_id: catererId || null,
      page: window.location.pathname,
      search_keyword: isSavedAddress
        ? (option.structured_formatting.main_text ?? '')
        : (option.description ?? ''),
      sub_category: trackingSubcategory,
      url: window.location.href,
      value: isSavedAddress ? 'saved address' : 'autocomplete address',
      misc_json: miscJson,
    });
  };

  const handleSelect = async (option: AutocompleteOption) => {
    if (option.place_id === MANUAL_ENTRY_OPTION_ID) {
      onManualEntrySelected?.();
      return;
    }

    const isSavedAddress = option.group === savedAddressesLabel;

    if (appBarStepThrough) {
      let updatedValues;
      if (isSavedAddress) {
        const savedAddress =
          savedAddresses.find(address => address.placeId === option.place_id) ?? null;
        updatedValues = {
          ...values,
          addressString: savedAddress?.secondaryText ?? '',
          ...(savedAddressName
            ? {
                [savedAddressName]: savedAddress,
              }
            : {}),
          [googlePlaceName]: null,
          [geocodedAddressResultName]: null,
        };
      } else {
        updatedValues = {
          ...values,
          ...(option.description ? {[inputName]: option.description} : {}),
          [googlePlaceName]: option,
          ...(savedAddressName ? {[savedAddressName]: null} : {}),
        };
      }
      trackAddressSelected(option, isSavedAddress);

      /*
        For the app bar step through, we need to validate the address before stepping into the next
        section. The form's state values will not be updated before we run the validation,
        so we need to call the validation at the same time as setting the form's state using the same
        props for both.
      */
      setValues(updatedValues);

      if (validateAddress) {
        const {addressIsValid} = await validateAddress(updatedValues);

        if (!addressIsValid) {
          return;
        }
      }
      return fulfillmentDetailClickHandler(FulfillmentDetailStates.DATETIME);
    }

    if (isSavedAddress) {
      savedAddressHelpers.setValue(
        savedAddresses.find(address => address.placeId === option.place_id) ?? null,
      );
      googlePlaceHelpers.setValue(null);
      geocodedAddressResultHelpers.setValue(null);
    } else {
      googlePlaceHelpers.setValue(option);
      savedAddressHelpers.setValue(null);
    }
    trackAddressSelected(option, isSavedAddress);

    if (shouldSubmitOnSelect) submitForm();
  };

  // Someone pressed Enter on a freeform address in the app bar step through; validate what they
  // typed. Enter on a highlighted option is handled by the option's onClick instead.
  const handleInputKeyDown = async (event: InputKeyDownEvent) => {
    slotProps?.input?.onKeyDown?.(event);
    selectOnNextClickRef.current = false;
    if (!appBarStepThrough || event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    if (highlightedItemRef.current) return;
    if (!inputValue || inputValue === googlePlace?.description) return;

    event.preventDefault();
    if (validateAddress) {
      const {addressIsValid} = await validateAddress(values);

      if (!addressIsValid) {
        return;
      }
    }
    fulfillmentDetailClickHandler(FulfillmentDetailStates.DATETIME);
  };

  const handleValueChange = (newInputValue: string, {reason}: ValueChangeDetails) => {
    // Base UI clears the input on Escape while the popup is closed, which would drop a typed
    // address (e.g. Escape before predictions load). Keep it; the clear button still clears.
    if (reason === 'escape-key') return;
    if (reason === 'input-change') setHasTypedSinceOpen(true);
    // Picking manual entry would otherwise write its label into the field after
    // onManualEntrySelected has cleared it.
    if (
      manualEntryOption &&
      reason !== 'input-change' &&
      newInputValue === manualEntryOption.description
    ) {
      return;
    }
    // A blank input (typed away or cleared) goes back to the empty state.
    if (!newInputValue) openListboxEmpty();
    inputHelpers.setValue(newInputValue);
    // Clear selections when input is emptied (e.g., clear button) regardless of reason
    if (!newInputValue) {
      googlePlaceHelpers.setValue(null);
      geocodedAddressResultHelpers.setValue(null);
    } else if (reason === 'input-change') {
      // Only clear selections when user is typing, not when value is filled in by a selection
      if (googlePlace?.description !== newInputValue) {
        googlePlaceHelpers.setValue(null);
      }
      if (inputValue !== newInputValue) {
        geocodedAddressResultHelpers.setValue(null);
      }
    }
    if (trackBeganSearchKeyword) {
      track('began entering search keyword for address search', {
        page: window.location.pathname,
        sub_category: trackingSubcategory,
        caterer_id: catererId,
        url: window.location.href,
        misc_json: miscJson,
      });
      setTrackBeganSearchKeyword(false);
    }
    if (onInputChange) onInputChange(newInputValue);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    // Base UI only clears the highlight after the popup's exit transition, so an Enter in between
    // would still look like it's on an option.
    if (!nextOpen) highlightedItemRef.current = undefined;
    if (!nextOpen) setHasTypedSinceOpen(false);
    setIsOpen(nextOpen);
    setTrackShownSavedAddresses(nextOpen);
    onOpenChange?.(nextOpen);
  };

  // A focused, blank input always shows the empty state — Base UI only opens on click or typing,
  // so keyboard focus would otherwise leave it closed.
  const openListboxEmpty = () => {
    if (!experimentVariation || disabled) return;
    handleOpenChange(true);
  };

  // setSelectionRange rather than select(): iOS Safari ignores select() on focus
  const selectAllInputText = (input: HTMLInputElement) => {
    input.setSelectionRange(0, input.value.length);
  };

  const handleInputFocus = (event: InputFocusEvent) => {
    slotProps?.input?.onFocus?.(event);
    if (experimentVariation) {
      selectAllInputText(event.currentTarget);
      selectOnNextClickRef.current = true;
    }
    if (inputValue === '') openListboxEmpty();
  };

  // fix the pointer user issue on iOS Safari where the input is focused but the text is not selected on first click
  const handleInputClick = (event: InputClickEvent) => {
    slotProps?.input?.onClick?.(event);
    if (!selectOnNextClickRef.current) return;
    selectOnNextClickRef.current = false;
    const input = event.currentTarget;
    setTimeout(() => selectAllInputText(input));
  };

  // Fetch google place predictions
  useEffect(() => {
    let active = true;
    if (inputValue === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setGooglePlaceOptions([]);
      setIsLoading(false);
      return;
    }
    // Without the Places service the fetch is a no-op, so don't report a search that never ends.
    setIsLoading(Boolean(autocompleteService));
    fetchGooglePlacePredictions(
      {input: inputValue, types: ['geocode'], componentRestrictions: {country: 'us'}},
      results => {
        if (active) {
          setGooglePlaceOptions((results as unknown as AutocompleteOption[] | null) ?? []);
          setIsLoading(false);
        }
      },
    );
    return () => {
      active = false;
    };
  }, [autocompleteService, fetchGooglePlacePredictions, inputValue]);

  useEffect(() => {
    if (trackShownSavedAddresses && !showListboxEmpty && savedAddresses?.length > 0) {
      track('shown saved addresses', {
        caterer_id: catererId || null,
        page: window.location.pathname,
        sub_category: trackingSubcategory,
        url: window.location.href,
        misc_json: miscJson,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTrackShownSavedAddresses(false);
    }
  }, [
    track,
    trackShownSavedAddresses,
    showListboxEmpty,
    savedAddresses?.length,
    catererId,
    miscJson,
    trackingSubcategory,
  ]);

  return (
    <Autocomplete.Root
      disabled={disabled}
      filter={null}
      filteredItems={optionGroups}
      itemToStringValue={(option: AutocompleteOption) => option.description}
      items={optionGroups}
      onItemHighlighted={(item: AutocompleteOption | undefined) => {
        highlightedItemRef.current = item;
      }}
      onOpenChange={handleOpenChange}
      onValueChange={handleValueChange}
      open={(open ?? isOpen) && hasPopupContent}
      openOnInputClick
      value={inputValue}
    >
      <Autocomplete.InputGroup
        className={twMerge(
          error &&
            'border-guava-400 bg-guava-50 hover:border-guava-500 hover:bg-guava-50 active:border-guava-600',
          slotProps?.inputGroup?.className,
        )}
      >
        {prefix === undefined ? (
          <span className="shrink-0">
            <Icon className="text-peppercorn-600" icon={faMapMarkerAlt} size="xsmall" />
          </span>
        ) : (
          prefix
        )}
        <Autocomplete.Input
          aria-label={t('components.AddressSearchAutocomplete.aria.label')}
          placeholder={t('components.AddressSearchAutocomplete.placeholder')}
          {...slotProps?.input}
          aria-invalid={error || undefined}
          className={twMerge('w-full truncate', slotProps?.input?.className)}
          id={id}
          onBlur={event => {
            slotProps?.input?.onBlur?.(event);
            handleOpenChange(false);
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          onClick={handleInputClick}
        />
        <Autocomplete.Clear
          aria-label={t('components.AddressSearchAutocomplete.aria.clear')}
          // Change the X icon for the experiment variation to match the design spec.
          render={
            experimentVariation
              ? ({className: _className, ...props}) => (
                  <Button {...props} className="rounded-full p-1" variant="secondary">
                    <Icon icon={faXmark} size="xsmall" />
                  </Button>
                )
              : undefined
          }
        />
        {showLocationButton && (
          <div className={experimentVariation && 'mr-1'}>
            <AddressSearchAutocompleteSuffix
              geocodedAddressResultHelpers={geocodedAddressResultHelpers}
              inputHelpers={inputHelpers}
              icon={experimentVariation ? faLocationArrow : undefined}
            />
          </div>
        )}
        {error && (
          <span aria-hidden className="shrink-0 text-guava-400">
            <Icon icon={faTriangleExclamation} size="small" />
          </span>
        )}
      </Autocomplete.InputGroup>
      <Autocomplete.Status className="sr-only">
        {isLoading ? t('components.AddressSearchAutocomplete.locator.aria.loading') : null}
      </Autocomplete.Status>
      {hasPopupContent && (
        <Autocomplete.Content
          anchor={slotProps?.content?.anchor}
          className={twMerge(
            experimentVariation && 'rounded-2xl border border-peppercorn-100 shadow-lg',
            slotProps?.content?.className,
          )}
          sideOffset={slotProps?.content?.sideOffset}
          style={
            // Force to always display the dropdown below the input.
            experimentVariation
              ? ({side}) =>
                  side === 'top'
                    ? {
                        translate: `0 calc(100% + var(--anchor-height) + ${2 * contentSideOffset}px)`,
                      }
                    : {}
              : undefined
          }
        >
          {experimentVariation && showListboxEmpty ? (
            <AddressSearchAutocompleteExperimentEmptyListbox
              currentLocationOptionId={`${id}-current-location`}
              geocodedAddressResultHelpers={geocodedAddressResultHelpers}
              inputHelpers={inputHelpers}
              onAddressFilled={() => handleOpenChange(false)}
              // Keep focus in the input so the dropdown stays open for the current-location click.
              onMouseDown={event => event.preventDefault()}
              variation={experimentVariation}
            />
          ) : (
            <>
              <Autocomplete.List
                className={twMerge(
                  'max-h-[400px] p-3 pb-0',
                  experimentVariation && 'flex flex-col gap-1 p-6 pb-0',
                )}
              >
                {(group: AutocompleteOptionGroup) => (
                  <Autocomplete.Group key={group.id} items={group.items}>
                    {group.label && (
                      <Autocomplete.GroupLabel>{group.label}</Autocomplete.GroupLabel>
                    )}
                    <Autocomplete.Collection>
                      {(option: AutocompleteOption) => (
                        <Autocomplete.Item
                          key={option.place_id}
                          className={twMerge(
                            'px-2 py-1',
                            experimentVariation &&
                              'py-2 data-[highlighted]:bg-peppercorn-50 hover:bg-peppercorn-50',
                            slotProps?.item?.className,
                          )}
                          onClick={() => handleSelect(option)}
                          value={option}
                        >
                          {option.place_id === MANUAL_ENTRY_OPTION_ID ? (
                            <span className="flex items-center gap-2 py-1 text-sm font-bold text-peppercorn-800">
                              <Icon icon={faPlus} size="xsmall" />
                              {option.description}
                            </span>
                          ) : experimentVariation ? (
                            <AddressSearchAutocompleteExperimentOption
                              isSavedAddress={group.id === 'saved'}
                              option={option}
                            />
                          ) : (
                            <AddressSearchAutocompleteOption
                              isSavedAddress={group.id === 'saved'}
                              option={option}
                            />
                          )}
                        </Autocomplete.Item>
                      )}
                    </Autocomplete.Collection>
                  </Autocomplete.Group>
                )}
              </Autocomplete.List>
              <div className="mb-3 mr-5 mt-1 flex justify-end">
                <Image
                  alt={t('components.AddressSearchAutocomplete.poweredByGoogle')}
                  height="15"
                  src={poweredByGoogleImage.src}
                  width="120"
                />
              </div>
            </>
          )}
        </Autocomplete.Content>
      )}
    </Autocomplete.Root>
  );
}
